import { useMemo } from 'react';
import {
  Table,
  Loading,
  SearchPanel,
  Container,
  Form,
  FormItem,
  AutoComplete,
  Button,
  CountryFilterDropdown,
  TextField,
} from '../../../../components';
import type { FilterValueProps } from '../../../../components/CountryFilterDropdown';
import type { ColumnType } from '../../../../components/Table';
import { useCampaignTasks } from './hooks/useCampaignTasks';
import { useCampaignVisitStats } from './hooks/useCampaignVisitStats';
import { VisitStatsCell, CampaignVisitsDetailsModal } from './components';
import { formatUrlForDisplay } from './utils/format-url';

type ExtendedTaskDetails = {
  url: string;
  demo_url: string | null;
  bdr_info_page_url?: string | null;
  campaign_name: string | null;
  company_name: string | null;
  last_visited_at?: string;
  visited_count?: number;
  id?: string;
};

export const CampaignTab = () => {
  const {
    tasks,
    loading,
    pagination,
    filters,
    setFilters,
    setPage,
    campaignOptions: campaignOptionsFromHook,
    countryOptions: countryOptionsFromHook,
    selectedTask,
    isModalOpen,
    handleDetailsClick,
    handleCloseModal,
  } = useCampaignTasks();

  const visitStatsMap = useCampaignVisitStats(tasks || [], filters);

  const hasCampaignFilter = !!filters?.campaign_name;

  const tableData = useMemo(() => {
    if (!tasks || !Array.isArray(tasks)) return [];
    return tasks.map((task, index) => {
      const taskId = task.id || index.toString();
      const visitStats = visitStatsMap.get(taskId);

      return {
        ...task,
        id: taskId,
        last_visited_at: visitStats?.last_visited_at,
        visited_count: visitStats?.visited_count,
        isLoadingVisitStats: visitStats?.isLoading ?? false,
        visitStatsError: visitStats?.error ?? false,
      };
    });
  }, [tasks, visitStatsMap]);

  const columns = useMemo((): ColumnType<ExtendedTaskDetails>[] => {
    const baseColumns: ColumnType<ExtendedTaskDetails>[] = [
      {
        header: 'URL',
        cellClassName: 'min-w-[5rem] max-w-[15rem] text-left',
        getValue: (row: ExtendedTaskDetails) => (
          <TextField
            text={row.url}
            href={row.url}
            formatTextForDisplay={formatUrlForDisplay}
          />
        ),
      },
      {
        header: 'Demo URL',
        cellClassName: 'min-w-[5rem] max-w-[15rem] text-left',
        getValue: (row: ExtendedTaskDetails) => (
          <TextField
            text={row.demo_url}
            href={row.demo_url || null}
            formatTextForDisplay={formatUrlForDisplay}
          />
        ),
      },
      {
        header: 'BDR Info Page URL',
        cellClassName: 'min-w-[5rem] max-w-[15rem] text-left',
        getValue: (row: ExtendedTaskDetails) => (
          <TextField
            text={row.bdr_info_page_url || null}
            href={row.bdr_info_page_url || null}
            formatTextForDisplay={formatUrlForDisplay}
          />
        ),
      },
    ];

    if (!hasCampaignFilter) {
      baseColumns.push({
        header: 'Campaign Name',
        cellClassName: 'min-w-[10rem] max-w-[15rem] text-left truncate',
        getValue: (row: ExtendedTaskDetails) => (
          <TextField text={row.campaign_name} />
        ),
      });
    }

    baseColumns.push(
      {
        header: 'Company Name',
        cellClassName: 'min-w-[10rem] max-w-[15rem] text-left truncate',
        getValue: (row: ExtendedTaskDetails) => (
          <TextField text={row.company_name} />
        ),
      },
      {
        header: 'Last Visited At',
        cellClassName: 'min-w-[8rem] max-w-[10rem] text-left',
        getValue: (
          row: ExtendedTaskDetails & {
            isLoadingVisitStats?: boolean;
            visitStatsError?: boolean;
          }
        ) => (
          <VisitStatsCell
            lastVisitedAt={row.last_visited_at}
            isLoading={row.isLoadingVisitStats ?? false}
            hasError={row.visitStatsError ?? false}
            type="date"
          />
        ),
      },
      {
        header: 'Visited Count',
        cellClassName: 'min-w-[8rem] text-center',
        getValue: (
          row: ExtendedTaskDetails & {
            isLoadingVisitStats?: boolean;
            visitStatsError?: boolean;
          }
        ) => (
          <VisitStatsCell
            visitedCount={row.visited_count}
            isLoading={row.isLoadingVisitStats ?? false}
            hasError={row.visitStatsError ?? false}
            type="count"
          />
        ),
      },
      {
        header: '',
        cellClassName: 'min-w-[8rem] text-center',
        getValue: (row: ExtendedTaskDetails) => {
          const fullTask = tasks?.find(t => t.id === row.id);
          if (!fullTask) {
            return <span>-</span>;
          }
          return (
            <Button
              label="Details"
              size="small"
              onClick={() => handleDetailsClick(fullTask)}
            />
          );
        },
      }
    );

    return baseColumns;
  }, [tasks, handleDetailsClick, hasCampaignFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loading />
      </div>
    );
  }

  return (
    <div className="p-4">
      <Form initialValue={filters ?? {}} onChange={setFilters}>
        <Container className="mb-4 w-full lg:w-3/4 xl:w-1/2 2xl:w-1/3">
          <div className="mb-4 flex flex-col gap-4 md:flex-row">
            <FormItem inline={false} name="campaign_name" label="Campaign Name">
              <AutoComplete
                options={campaignOptionsFromHook}
                placeholder="Select campaign"
              />
            </FormItem>
            <div className="flex flex-col gap-2">
              <label className="text-medium font-medium">
                Exclude Countries
              </label>
              <CountryFilterDropdown
                excludeOnly
                label="Country Filter"
                buttonClassName="h-[40px] px-3"
                badgeClassName="w-8 h-8 text-tiny"
                relatedCountries={countryOptionsFromHook.map(opt => opt.value)}
                value={{
                  selected: filters?.exclude_countries || [],
                  mode: 'EXCLUDE',
                }}
                onChange={({ selected }: FilterValueProps) => {
                  setFilters({
                    ...filters,
                    exclude_countries:
                      selected.length > 0 ? selected : undefined,
                  });
                }}
              />
            </div>
          </div>
        </Container>
      </Form>

      {
        <>
          <div className="overflow-x-auto">
            <Table
              data={tableData}
              loading={loading}
              emptyMessage="No campaign tasks found"
              config={{
                columns,
              }}
            />
          </div>
          <div className="mt-sm flex justify-between items-center">
            <SearchPanel
              totalItems={pagination?.total_items}
              page={pagination?.page}
              perPage={10}
              withPaginate
              setPage={setPage}
            />
          </div>
        </>
      }

      <CampaignVisitsDetailsModal
        task={selectedTask}
        open={isModalOpen}
        onClose={handleCloseModal}
        excludeCountries={filters?.exclude_countries}
      />
    </div>
  );
};
